import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../ui/select';

const API = process.env.REACT_APP_BACKEND_URL || '';

import { OptionalProfileSection, EMPTY_PROFILE, profileToPayload, validateProfile } from './CandidateProfileFields';

const EMPTY = { job_id: '', name: '', email: '', phone: '', notes: '' };
const currentRole = () => { try { return JSON.parse(localStorage.getItem('user') || '{}').role; } catch { return ''; } };

export const AddCandidateDialog = ({ open, onOpenChange, jobs, onCreated }) => {
  const [form, setForm] = useState(EMPTY);
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [saving, setSaving] = useState(false);
  const canViewCtc = ['admin', 'hr_manager'].includes(currentRole());

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    if (!form.job_id) return toast.error('Please select a job opening');
    if (!form.name.trim() || form.name.trim().length < 2) return toast.error('Please enter the candidate name');
    if (!form.email.trim()) return toast.error('Please enter the candidate email');
    const profileErr = validateProfile(profile);
    if (profileErr) return toast.error(profileErr);

    setSaving(true);
    try {
      const payload = {
        job_id: form.job_id,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
        ...profileToPayload(profile, canViewCtc),
      };
      await axios.post(`${API}/api/recruitment/candidates`, payload);
      toast.success(`${payload.name} added to the pipeline`);
      setForm(EMPTY);
      setProfile(EMPTY_PROFILE);
      onOpenChange(false);
      onCreated && onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add candidate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-candidate-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">Add Candidate</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Job Opening *</Label>
            <Select value={form.job_id} onValueChange={(v) => set('job_id', v)}>
              <SelectTrigger data-testid="add-candidate-job-select">
                <SelectValue placeholder="Select a job opening" />
              </SelectTrigger>
              <SelectContent>
                {jobs.map(job => (
                  <SelectItem key={job.job_id} value={job.job_id}>
                    {job.title} ({job.job_id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Full Name *</Label>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Jane Smith" data-testid="add-candidate-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)}
              placeholder="jane@example.com" data-testid="add-candidate-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(e) => set('phone', e.target.value)}
              placeholder="(555) 123-4567" data-testid="add-candidate-phone-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)}
              placeholder="Referral source, initial impressions..." rows={2}
              data-testid="add-candidate-notes-input" />
          </div>
          <OptionalProfileSection value={profile} onChange={setProfile} showCtc={canViewCtc} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="add-candidate-submit-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Add Candidate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddCandidateDialog;
